import { useId, type InputHTMLAttributes } from "react";
import { cx } from "./cx";
import { FIELD_HEIGHT, FieldLabel, FieldNote, type FieldSize } from "./fieldParts";

export interface PhoneFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "size" | "type"> {
  /** Default "Mobile number". */
  label?: string;
  /** Muted line under the field. Replaced by error when there is one. */
  hint?: string;
  error?: string;
  /** The 10 national digits, e.g. "9171234567". Shown as "917 123 4567". */
  value: string;
  /** Receives the 10 national digits. A pasted "+63…", "63…" or "0…" is normalised. */
  onChange: (digits: string) => void;
  size?: Exclude<FieldSize, "sm">;
}

/** "0917 123 4567" / "+63 917 123 4567" → "9171234567" */
export function toNationalPH(raw: string): string {
  let digits = raw.replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("63")) digits = digits.slice(2);
  if (digits.startsWith("0")) digits = digits.slice(1);
  return digits.slice(0, 10);
}

/** "9171234567" → "917 123 4567" */
export function formatPH(digits: string): string {
  const national = toNationalPH(digits);
  return [national.slice(0, 3), national.slice(3, 6), national.slice(6, 10)].filter(Boolean).join(" ");
}

/** A PH mobile number: 10 digits starting with 9. */
export const isMobilePH = (digits: string) => /^9\d{9}$/.test(digits);

/** Philippine mobile number. Fixed +63 prefix, numeric keypad, formatted as typed. */
export function PhoneField({ label = "Mobile number", hint, error, value, onChange, size = "lg", id, disabled, className, ...rest }: PhoneFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const noteId = `${inputId}-note`;

  return (
    <label htmlFor={inputId} className={cx("block", className)}>
      {label && (
        <FieldLabel>
          {label}
          <span className="sr-only">, starting +63</span>
        </FieldLabel>
      )}
      <span
        className={cx(
          "flex items-stretch overflow-hidden rounded-sm border inset-shadow-default [transition:var(--transition-control)]",
          FIELD_HEIGHT[size],
          disabled ? "bg-paper-2" : "bg-surface-card",
          error ? "border-danger-500" : "border-border-default focus-within:border-green-700",
          "focus-within:shadow-focus",
        )}
      >
        <span aria-hidden="true" className="flex items-center border-r border-border-default bg-paper-2 px-3 type-sku [font-size:var(--text-base)] text-text-body">
          +63
        </span>
        <input
          id={inputId}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder="917 123 4567"
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? noteId : undefined}
          value={formatPH(value)}
          onChange={(event) => onChange(toNationalPH(event.target.value))}
          className="min-w-0 flex-1 border-none bg-transparent px-3 type-sku [font-size:var(--text-base)] tracking-wide text-text-strong outline-none disabled:cursor-not-allowed"
          {...rest}
        />
      </span>
      <FieldNote id={noteId} error={error} hint={hint} />
    </label>
  );
}
